import { useMatomo } from '@datapunt/matomo-tracker-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';
import { useSelector } from 'react-redux';
import { Redirect, Route, useLocation, withRouter } from 'react-router-dom';
import { Container } from 'rsuite';

import { IPAGlobalAlert } from '../components/IPAGlobalAlert';
import { LinkAccessBanner } from '../components/LinkAccessBanner';
import Menu from '../components/Menu';
import NavBar from '../components/NavBar/NavBar';
import NavBarAdmin from '../components/NavBarAdmin';
import Warning from '../components/Warning';

import { useNotificationReport, usePriceListRerunNotification } from '../hooks';
import { getAlertas } from '../services/AlertaService';
import { canAccessIpaRoute, getIpaRouteFallbackPath } from '../utils/ipaAccess';
import { getUserDimensions } from '../utils/MatomoConfig';
import { clearSession, hasStoredSessionUser, reloadPage } from '../utils/sessionEnd';

const MemoNavBar = React.memo(NavBar);

const LOGIN_PATH = '/login';

// A recarga zera a marca de fim da carga antes de o proximo usuario carregar.
const EndSessionAndGoToLogin = ({ history }) => {
  useEffect(() => {
    clearSession();
    history.replace(LOGIN_PATH);
    reloadPage();
  }, [history]);

  return null;
};

const PrivateLayout = ({ children }) => {
  const location = useLocation();
  const [alertas, setAlertas] = useState(null);

  const { getRequestNotificationReport } = useNotificationReport();
  usePriceListRerunNotification();
  const getRequestNotificationReportRef = useRef(getRequestNotificationReport);

  useEffect(() => {
    getRequestNotificationReportRef.current = getRequestNotificationReport;
  }, [getRequestNotificationReport]);

  useEffect(() => {
    let isMounted = true;

    const getAllAlertasSafe = () => {
      getAlertas().then(response => {
        if (isMounted) {
          setAlertas(response);
        }
      });
    };

    getAllAlertasSafe();

    const interval = setInterval(async () => {
      getAllAlertasSafe();
    }, 300000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      getRequestNotificationReportRef.current();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const isAdminRoute = location.pathname.startsWith('/administrador');
  const showIPAAlert = false;
  const isIPARoute = showIPAAlert && location.pathname.startsWith('/ipa');

  return (
    <div className="show-fake-browser sidebar-page">
      <Container className={alertas?.some(item => item.url.includes(location.pathname)) ? 'first-container has-warning' : 'first-container'}>
        {children.props.adminArea ? <NavBarAdmin usuario={children?.props?.usuario} /> : <MemoNavBar usuario={children?.props?.usuario} />}
        {alertas?.some(item => item.url.includes(location.pathname)) ? (
          <Warning
            title="Atenção"
            description={
              alertas.find(item => item.url.includes(location.pathname)) ? alertas.find(item => item.url.includes(location.pathname)).mensagem : null
            }
          />
        ) : null}
        {!isAdminRoute && <Menu usuario={children?.props?.usuario} />}
        <LinkAccessBanner>
          {isIPARoute ? (
            <div className="ipa-private-page-content">
              <IPAGlobalAlert />
              {children}
            </div>
          ) : (
            children
          )}
        </LinkAccessBanner>
      </Container>
    </div>
  );
};

const PrivateRoute = ({ component: Component, history, ...rest }) => {
  const { trackPageView, pushInstruction } = useMatomo();
  const { setPathPageView, setIdentity } = useTrackingCode();
  const userLoadFinished = useSelector(state => state.clienteDataReducer.clienteDataLoadFinished);

  pushInstruction('setUserId', JSON.parse(localStorage.getItem('usuario'))?.email);

  function handlePushIntruction(user) {
    const dimensions = getUserDimensions(user);

    dimensions.forEach(item => {
      pushInstruction(item.instructionType, item.dimensionId, item.dimensionValue);
    });
  }

  useEffect(() => {
    setPathPageView(window.location.pathname);
    setIdentity(rest?.usuario?.login);
  }, [window.location.pathname, rest?.usuario?.login]);

  useEffect(() => {
    if (rest.usuario) {
      handlePushIntruction(rest.usuario);
    }
  }, [rest.usuario]);

  useEffect(() => {
    trackPageView();
  }, []);

  return (
    <Route
      {...rest}
      render={props => {
        const isAuthenticated = localStorage.getItem('autenticacao.token') && localStorage.getItem('autenticacao.token') !== 'null';

        if (isAuthenticated && rest.ipaRouteAccess && !rest.usuario) {
          // Sem usuario gravado nao ha carga a esperar (perfil sem produto depois do login).
          const userLoadEndedWithoutUser = !hasStoredSessionUser() || userLoadFinished;
          return userLoadEndedWithoutUser ? <EndSessionAndGoToLogin history={history} /> : null;
        }

        if (isAuthenticated && !canAccessIpaRoute(rest.usuario, rest.ipaRouteAccess)) {
          return (
            <Redirect
              to={{
                pathname: getIpaRouteFallbackPath(rest.usuario, rest.ipaRouteAccess),
                state: { from: history.location.pathname },
              }}
              from={history.location.pathname}
            />
          );
        }

        return isAuthenticated ? (
          <PrivateLayout>
            <Component {...props} {...rest} />
          </PrivateLayout>
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: history.location.pathname },
            }}
            from={history.location.pathname}
          />
        );
      }}
    />
  );
};

export default withRouter(PrivateRoute);
